import { memo, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { type FileStatus } from '@air/feature-file-status/constants';
import {
  FileStatusTrackingPanel,
  type FileStatusTrackingPanelProps,
  type FileTrackingItem,
} from '@air/feature-file-status/ui';
import { useUploadEstimatedTime, useUploadsTrackingSession } from '@air/feature-uploads/hooks';
import {
  convertUploadToFileTrackingPaneItem,
  type GetUploadHeaderTitlesArgs,
  type GetUploadHeaderTitlesResult,
  getUploadHeaderTitles,
} from '@air/feature-uploads/utils';
import {
  allUploadProgressSelector,
  hasUploadsSelector,
  setUploadsResumedAction,
  type UploaderStore,
  uploadArraySelector,
  uploadByIdSelector,
  uploadingSpeedSelector,
} from '@air/redux-uploader';
import {
  type UseDeleteUploadParams,
  useDeleteAllUploads,
  useDeleteUpload,
  usePauseAllUploads,
  usePauseUploadById,
  useResumeAllUploads,
  useRetryAllUploads,
} from '@air/upload-utils';

export type UploadsTrackingPanelProps = {
  summaryLocation: string | undefined;
  onCancelAllUploads?: () => void;
  onRetryAll?: (args: { retryAllUploads: () => void }) => void;
  onDeleteUpload?: UseDeleteUploadParams['onDelete'];
  getStatusTitle?: (
    args: GetUploadHeaderTitlesArgs & { status: FileStatus },
  ) => GetUploadHeaderTitlesResult[FileStatus];
} & Pick<
  FileStatusTrackingPanelProps,
  | 'className'
  | 'style'
  | 'disableDismiss'
  | 'disableToggle'
  | 'getSummaryTrackingAvatar'
  | 'isExpanded'
  | 'getCompletedItemsToDisplay'
  | 'getErroredItemsToDisplay'
  | 'getAbortedItemsToDisplay'
  | 'getStoppedItemsToDisplay'
  | 'getOngoingItemsToDisplay'
>;

export const UploadsTrackingPanel = memo(
  ({
    summaryLocation,
    onCancelAllUploads,
    onDeleteUpload,
    getStatusTitle,
    onRetryAll,
    ...fileStatusTrackingPanelProps
  }: UploadsTrackingPanelProps) => {
    const dispatch = useDispatch();
    const store = useStore<UploaderStore>();

    const { isOnline } = useUploadsTrackingSession();
    const { estimatedTime } = useUploadEstimatedTime();
    const uploadSpeed = useSelector(uploadingSpeedSelector);

    const hasUploads = useSelector(hasUploadsSelector);

    const uploads = useSelector(uploadArraySelector);
    const uploadProgress = useSelector(allUploadProgressSelector);
    const uploadsItems = uploads.map((upload) =>
      convertUploadToFileTrackingPaneItem(upload, uploadProgress[upload.id]),
    );

    const { pauseUploadById } = usePauseUploadById();
    const { deleteUpload } = useDeleteUpload({ onDelete: onDeleteUpload });
    const { deleteAllUploads } = useDeleteAllUploads({ onDelete: onDeleteUpload });
    const { pauseAllUploads } = usePauseAllUploads();
    const { resumeAllUploads } = useResumeAllUploads();
    const { retryAllUploads } = useRetryAllUploads();

    const cancelItem = useCallback(
      (item: FileTrackingItem) => {
        const upload = uploadByIdSelector(store.getState(), item.id);

        if (upload) {
          void deleteUpload(upload);
        }
      },
      [deleteUpload, store],
    );

    const pauseItem = useCallback(
      (item: FileTrackingItem) => {
        void pauseUploadById(item.id);
      },
      [pauseUploadById],
    );

    const resumeItem = useCallback(
      (item: FileTrackingItem) => {
        dispatch(setUploadsResumedAction({ uploadIds: [item.id] }));
      },
      [dispatch],
    );

    const tryToCancelAll = useCallback(() => {
      if (onCancelAllUploads) {
        onCancelAllUploads();
      } else {
        void deleteAllUploads();
      }
    }, [deleteAllUploads, onCancelAllUploads]);

    const handleRetryAll = useCallback(() => {
      if (onRetryAll) {
        onRetryAll({ retryAllUploads });
      } else {
        retryAllUploads();
      }
    }, [onRetryAll, retryAllUploads]);

    const getMemoedUploadHeaderTitle: FileStatusTrackingPanelProps['getStatusTitle'] = useCallback(
      (counts, status) => {
        const args = {
          counts,
          estimatedCompletionTime: estimatedTime,
          isOnline,
          speed: uploadSpeed,
          summaryLocation,
        };

        if (getStatusTitle) {
          return getStatusTitle({ ...args, status });
        }

        return getUploadHeaderTitles(args)[status];
      },
      [getStatusTitle, estimatedTime, isOnline, summaryLocation, uploadSpeed],
    );

    return hasUploads ? (
      <FileStatusTrackingPanel
        getStatusTitle={getMemoedUploadHeaderTitle}
        items={uploadsItems}
        onCancelAll={tryToCancelAll}
        onCancelItem={cancelItem}
        onDismiss={tryToCancelAll}
        onPauseAll={pauseAllUploads}
        onPauseItem={pauseItem}
        onResumeAll={isOnline ? resumeAllUploads : undefined}
        onResumeItem={isOnline ? resumeItem : undefined}
        onRetryAll={handleRetryAll}
        onRetryItem={resumeItem}
        testId="UPLOADER_PANE"
        {...fileStatusTrackingPanelProps}
      />
    ) : null;
  },
);

UploadsTrackingPanel.displayName = 'UploadsTrackingPanel';
